import React from "react";
import ReactDOM from "react-dom";
import usePrevious from "../../lib/hooks/usePrevious";

const isFirefox = window.navigator.userAgent.toLowerCase().indexOf("firefox") > -1;

const printable = (options: Highcharts.Options): Highcharts.Options => ({
  ...options,
  ...(options.chart?.scrollablePlotArea
    ? {
        chart: {
          ...options.chart,
          width: options.chart.scrollablePlotArea.minWidth,
          fitWrapperWidth: true,
          scrollablePlotArea: undefined,
        },
      }
    : {}),
  ...(isFirefox
    ? {
        legend: {
          ...options.legend,
          useHTML: false,
        },
        xAxis: Array.isArray(options.xAxis)
          ? options.xAxis.map(xAxisOptions => ({
              ...xAxisOptions,
              labels: { ...xAxisOptions.labels, useHTML: false },
              title: { ...xAxisOptions.title, useHTML: false },
            }))
          : {
              ...options.xAxis,
              labels: { ...options.xAxis?.labels, useHTML: false },
              title: { ...options.xAxis?.title, useHTML: false },
            },
        yAxis: Array.isArray(options.yAxis)
          ? options.yAxis.map(yAxisOptions => ({
              ...yAxisOptions,
              labels: { ...yAxisOptions.labels, useHTML: false },
              title: { ...yAxisOptions.title, useHTML: false },
            }))
          : {
              ...options.yAxis,
              labels: { ...options.yAxis?.labels, useHTML: false },
              title: { ...options.yAxis?.title, useHTML: false },
            },
        deferSeriesRender: false,
      }
    : {}),
});

const usePrintableOptions = (options: Highcharts.Options) => {
  const [print, setPrint] = React.useState(false);
  const resetNeeded = print !== usePrevious(print) && Boolean(options.chart?.scrollablePlotArea);

  React.useLayoutEffect(() => {
    const beforeprint = () => {
      // flushSync needed for Firefox, it triggers afterprint event right after beforeprint event
      // React batches both updates and hook would never update with print == true
      ReactDOM.flushSync(() => {
        setPrint(true);
      });
    };
    const afterprint = () => {
      ReactDOM.flushSync(() => {
        setPrint(false);
      });
    };
    window.addEventListener("beforeprint", beforeprint);
    window.addEventListener("afterprint", afterprint);
    return () => {
      window.removeEventListener("beforeprint", beforeprint);
      window.removeEventListener("afterprint", afterprint);
    };
  }, []);

  return [print ? printable(options) : options, resetNeeded] as const;
};

export default usePrintableOptions;
